import React, { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { API_URL } from "../config/env";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import * as adbs from "ad-bs-converter";

interface SiteSettings {
  email?: string;
  phone?: string;
  address?: string;
  mapUrl?: string;
}

const PublicLayout: React.FC = () => {
  const { isAuthenticated, logout } = useAuth();
  const { t, language, setLanguage } = useLanguage();

  const handleLogout = () => {
    logout();
  };

  const toggleLanguage = () => {
    setLanguage(language === "en" ? "ne" : "en");
  };

  // =========================
  // DATE & TIME
  // =========================

  const [now, setNow] = useState<Date>(new Date());
  const [bsDate, setBsDate] = useState<string>("");

  // Update time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Convert AD date to BS date
// Convert AD date to BS date
useEffect(() => {
  try {
    const nepalDate = new Date(
      now.toLocaleString("en-US", {
        timeZone: "Asia/Kathmandu",
      })
    );

    const y = nepalDate.getFullYear();
    const m = nepalDate.getMonth() + 1;
    const d = nepalDate.getDate();

    const result = (adbs as any).ad2bs(
      `${y}/${m}/${d}`
    );



    if (result?.ne) {
      const formatted =
        `${result.ne.year} साल ${result.ne.strMonth} ${result.ne.day} गते ${result.ne.strDayOfWeek}`;

      setBsDate(formatted);
    }

  } catch (error) {
    console.error("BS date conversion error:", error);
    setBsDate("");
  }

}, [now]);


  // =========================
  // ENGLISH DATE
  // =========================

  const englishDate = now.toLocaleDateString("en-US", {
  timeZone: "Asia/Kathmandu",
  year: "numeric",
  month: "long",
  day: "numeric",
});

  // =========================
  // NEPALI DATE
  // =========================

  const nepaliDate =
    bsDate || now.toLocaleDateString("ne-NP", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  // =========================
  // ENGLISH TIME
  // =========================

  const englishTime = now.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  // =========================
  // NEPALI TIME
  // =========================

  const nepaliTime = now.toLocaleTimeString("ne-NP", {
    timeZone: "Asia/Kathmandu",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  // =========================
  // FINAL DISPLAY DATE/TIME
  // =========================

  const displayDate =
    language === "en" ? englishDate : nepaliDate;

  const displayTime =
    language === "en" ? englishTime : nepaliTime;

  // =========================
  // MOBILE MENU
  // =========================

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Site settings
  const [siteSettings, setSiteSettings] =
    useState<SiteSettings | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await fetch(`${API_URL}/settings`);
        const data = await res.json();
        setSiteSettings(data);
      } catch (err) {
        console.error(err);
      }
    };

    loadSettings();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Header */}
      <header className="bg-white text-white shadow-sm border-b">

        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">

          {/* =====================================================
              DESKTOP HEADER
              ===================================================== */}

          <div className="hidden md:block">

            {/* Top Section:
                Left Logo
                Center Information
                Right Flag + Date + Time
            */}

            <div className="relative flex items-center justify-between min-h-[120px] py-4">

              {/* Left: Logo */}
              <div className="flex items-center space-x-2 flex-shrink-0">

                <Link to="/">
                  <img
                    src="\images\hellocm logo crop.png"
                    alt="Logo"
                    className="h-20 w-20 object-contain"
                  />
                </Link>

              </div>

              {/* Center:
                  Province / Office / Portal / Address
              */}

              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center text-[#fa0101] flex flex-col items-center space-y-1 w-[60%]">

                <p className="text-lg font-bold leading-tight">
                  {language === "en"
                    ? "Government of Bagamati Province"
                    : "बागमती प्रदेश सरकार"}
                </p>

                <p className="text-base font-semibold leading-tight">
                  {language === "en"
                    ? "Office of the Chief Minister and Council of Ministers"
                    : "मुख्यमन्त्री तथा मन्त्रिपरिषद्को कार्यालय"}
                </p>

                <p className="text-2xl font-semibold leading-tight">
                  {language === "en"
                    ? "Grievance Management Portal"
                    : "गुनासो व्यवस्थापन प्रणाली"}
                </p>

                <p className="text-lg leading-tight">
                  {language === "en"
                    ? "Hetauda, Nepal"
                    : "हेटौंडा, नेपाल"}
                </p>

              </div>

              {/* Right:
                  Flag + Date + Time
              */}

              <div className="flex flex-col items-end space-y-1 flex-shrink-0">

                <div className="flex items-center space-x-2">

                  <img
                    src="/images/nepal_flag.gif"
                    alt="Nepal Flag"
                    className="h-20 w-14 object-contain"
                  />

                  <span className="text-sm font-medium text-gray-800 text-right whitespace-nowrap">

                    {language === "en" ? englishDate : bsDate}

                    <br />

                    <span className="text-xs text-gray-800">
                      {displayTime}
                    </span>

                  </span>

                </div>

              </div>

            </div>


            {/* =====================================================
                DESKTOP NAVIGATION
                ===================================================== */}

            <div className="flex justify-between items-center min-h-[52px] py-2">

              {/* Navigation */}

              <nav className="flex items-center gap-2 lg:gap-4 text-lg font-semibold">

                <Link
                  to="/"
                  className="text-blue-900 hover:text-white hover:bg-blue-500 px-4 py-2 rounded-md transition duration-200"
                >
                  {t("nav.home")}
                </Link>

                <Link
                  to="/submit"
                  className="text-blue-900 hover:text-white hover:bg-blue-500 px-4 py-2 rounded-md transition duration-200"
                >
                  {t("grievances.submit")}
                </Link>

                <Link
                  to="/track"
                  className="text-blue-900 hover:text-white hover:bg-blue-500 px-4 py-2 rounded-md transition duration-200"
                >
                  {t("grievances.track")}
                </Link>

                <Link
                  to="/stats"
                  className="text-blue-900 hover:text-white hover:bg-blue-500 px-4 py-2 rounded-md transition duration-200"
                >
                  {t("grievances.statistic")}
                </Link>

              </nav>


              {/* Right Side:
                  Language & Auth
              */}

              <div className="flex items-center space-x-4">

                <button
                  onClick={toggleLanguage}
                  className="text-gray-700 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-medium"
                >
                  {language === "en" ? "नेपाली" : "English"}
                </button>


                {isAuthenticated ? (

                  <div className="flex items-center space-x-4">

                    <Link
                      to="/admin"
                      className="text-gray-700 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-medium"
                    >
                      {t("nav.dashboard")}
                    </Link>

                    <button
                      onClick={handleLogout}
                      className="text-gray-700 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-medium"
                    >
                      {t("nav.logout")}
                    </button>

                  </div>

                ) : (

                  <Link
                    to="/auth/login"
                    className="inline-block bg-[#2460b9] text-white px-4 py-2 rounded hover:bg-[#1e4f96] transition-colors"
                  >
                    {t("nav.login")}
                  </Link>

                )}

              </div>

            </div>

          </div>


          {/* =====================================================
              MOBILE HEADER
              ===================================================== */}

          <div className="md:hidden">

            {/* Mobile Top Section */}

            <div className="flex items-center justify-between gap-2 py-3">

              {/* Mobile Logo */}

              <div className="flex-shrink-0">

                <Link to="/">
                  <img
                    src="/images/New-emblwm.jpg"
                    alt="Logo"
                    className="h-16 w-16 object-contain"
                  />
                </Link>

              </div>


              {/* Mobile Center Information */}

              <div className="flex-1 min-w-0 text-center text-[#fa0101] px-1">

                <p className="text-[11px] sm:text-sm font-bold leading-tight">
                  {language === "en"
                    ? "Government of Bagamati Province"
                    : "बागमती प्रदेश सरकार"}
                </p>

                <p className="text-[10px] sm:text-xs font-semibold leading-tight mt-1">
                  {language === "en"
                    ? "Office of the Chief Minister and Council of Ministers"
                    : "मुख्यमन्त्री तथा मन्त्रिपरिषद्को कार्यालय"}
                </p>

                <p className="text-base sm:text-lg font-semibold leading-tight mt-1">
                  {language === "en"
                    ? "Grievance Management Portal"
                    : "गुनासो व्यवस्थापन प्रणाली"}
                </p>

                <p className="text-xs sm:text-sm leading-tight mt-1">
                  {language === "en"
                    ? "Hetauda, Nepal"
                    : "हेटौंडा, नेपाल"}
                </p>

              </div>


              {/* Mobile Flag */}

              <div className="flex-shrink-0">

                <img
                  src="/images/nepal_flag.gif"
                  alt="Nepal Flag"
                  className="h-16 w-11 object-contain"
                />

              </div>

            </div>


            {/* =====================================================
                MOBILE DATE & TIME
                ===================================================== */}

            <div className="flex justify-center border-t border-gray-100 py-2">

              <div className="text-center text-gray-800 text-xs sm:text-sm">

                <span>
                  {displayDate}
                </span>

                <span className="mx-2">
                  |
                </span>

                <span>
                  {displayTime}
                </span>
              </div>
            </div>


            {/* =====================================================
                MOBILE MENU BUTTON
                ===================================================== */}

            <div className="border-t border-gray-200 py-2">

              <button
                onClick={() =>
                  setMobileMenuOpen(!mobileMenuOpen)
                }
                className="w-full flex items-center justify-between px-3 py-2 text-blue-900 font-semibold rounded-md hover:bg-gray-100"
                aria-expanded={mobileMenuOpen}
                aria-label={
                  language === "en"
                    ? "Toggle navigation menu"
                    : "नेभिगेसन मेनु खोल्नुहोस्"
                }
              >

                <span>
                  {language === "en"
                    ? "Menu"
                    : "मेनु"}
                </span>

                <svg
                  className={`w-5 h-5 transition-transform duration-200 ${
                    mobileMenuOpen
                      ? "rotate-180"
                      : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>

              </button>


              {/* =====================================================
                  MOBILE NAVIGATION MENU
                  ===================================================== */}

              {mobileMenuOpen && (

                <nav className="mt-2 bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">

                  {/* Home */}

                  <Link
                    to="/"
                    onClick={closeMobileMenu}
                    className="block px-4 py-3 text-blue-900 font-semibold border-b border-gray-200 hover:bg-blue-500 hover:text-white"
                  >
                    {t("nav.home")}
                  </Link>


                  {/* Submit */}

                  <Link
                    to="/submit"
                    onClick={closeMobileMenu}
                    className="block px-4 py-3 text-blue-900 font-semibold border-b border-gray-200 hover:bg-blue-500 hover:text-white"
                  >
                    {t("grievances.submit")}
                  </Link>


                  {/* Track */}

                  <Link
                    to="/track"
                    onClick={closeMobileMenu}
                    className="block px-4 py-3 text-blue-900 font-semibold border-b border-gray-200 hover:bg-blue-500 hover:text-white"
                  >
                    {t("grievances.track")}
                  </Link>


                  {/* Statistics */}

                  <Link
                    to="/stats"
                    onClick={closeMobileMenu}
                    className="block px-4 py-3 text-blue-900 font-semibold border-b border-gray-200 hover:bg-blue-500 hover:text-white"
                  >
                    {t("grievances.statistic")}
                  </Link>


                  {/* Language */}

                  <button
                    onClick={toggleLanguage}
                    className="w-full text-left px-4 py-3 text-gray-700 font-medium border-b border-gray-200 hover:bg-gray-100"
                  >
                    {language === "en"
                      ? "नेपाली"
                      : "English"}
                  </button>


                  {/* Authentication */}

                  {isAuthenticated ? (

                    <>

                      <Link
                        to="/admin"
                        onClick={closeMobileMenu}
                        className="block px-4 py-3 text-gray-700 font-medium border-b border-gray-200 hover:bg-gray-100"
                      >
                        {t("nav.dashboard")}
                      </Link>

                      <button
                        onClick={() => {
                          closeMobileMenu();
                          handleLogout();
                        }}
                        className="w-full text-left px-4 py-3 text-gray-700 font-medium hover:bg-gray-100"
                      >
                        {t("nav.logout")}
                      </button>

                    </>

                  ) : (

                    <Link
                      to="/auth/login"
                      onClick={closeMobileMenu}
                      className="block mx-3 my-3 text-center bg-[#2460b9] text-white px-4 py-2 rounded hover:bg-[#1e4f96] transition-colors"
                    >
                      {t("nav.login")}
                    </Link>

                  )}

                </nav>

              )}

            </div>

          </div>

        </div>

      </header>


      {/* Main Content */}

      <main>
        <Outlet />
      </main>


      {/* Footer */}

      <footer className="bg-gray-800 text-white">

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">

            <div>

              <h3 className="text-lg font-semibold mb-4">
                GMS
              </h3>

              <p className="text-gray-100 text-sm">
                Grievance Management System
              </p>

            </div>


            <div>

              <h4 className="text-md font-semibold mb-4">
                Quick Links
              </h4>

              <ul className="space-y-2 text-sm text-gray-300">

                <li>
                  <Link
                    to="/"
                    className="hover:text-white"
                  >
                    {t("nav.home")}
                  </Link>
                </li>

                <li>
                  <Link
                    to="/submit"
                    className="hover:text-white"
                  >
                    {t("grievances.submit")}
                  </Link>
                </li>

                <li>
                  <Link
                    to="/track"
                    className="hover:text-white"
                  >
                    {t("grievances.track")}
                  </Link>
                </li>

              </ul>

            </div>


            <div>

              <h4 className="text-md font-semibold mb-4">
                Contact
              </h4>

              <ul className="space-y-2 text-sm text-gray-300">

                <li>
                  Email:{" "}
                  {siteSettings
                    ? siteSettings.email
                    : "Loading..."}
                </li>

                <li>
                  Phone:{" "}
                  {siteSettings
                    ? siteSettings.phone
                    : "Loading..."}
                </li>

                <li>
                  Address:{" "}
                  {siteSettings
                    ? siteSettings.address
                    : "Loading..."}
                </li>

              </ul>

            </div>


            <div>

              <h4 className="text-md font-semibold mb-4">
                Follow Us
              </h4>

              <div className="flex space-x-4">

                <a
                  href="#"
                  className="text-gray-300 hover:text-white"
                >
                  <span className="sr-only">
                    Facebook
                  </span>

                  <svg
                    className="h-6 w-6"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>

                </a>


                <a
                  href="#"
                  className="text-gray-300 hover:text-white"
                >
                  <span className="sr-only">
                    Twitter
                  </span>

                  <svg
                    className="h-6 w-6"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                  </svg>

                </a>

              </div>

            </div>


            {/* 5th Column: Google Map */}

            <div>

              <h4 className="text-md font-semibold mb-4">
                Our Location
              </h4>

              <div className="rounded-md overflow-hidden">

                {siteSettings?.mapUrl ? (

                  <iframe
                    src={siteSettings.mapUrl}
                    width="300"
                    height="200"
                    loading="lazy"
                  ></iframe>

                ) : (

                  <p>
                    mapURL Error
                  </p>

                )}

              </div>

            </div>

          </div>


          <div className="mt-8 pt-8 border-t border-gray-100 text-center text-sm text-gray-300">

            <p className="text-sm text-white">

              {new Date().getFullYear()} &copy;{" "}

              <a
                href="http://sarojacharya29.com.np"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:underline"
              >
                All rights reserved
              </a>

              .

            </p>

          </div>

        </div>

      </footer>

    </div>
  );
};

export default PublicLayout;